import type { ReactElement } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Minus,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type {
  CoverageCounts,
  CoverageStatus,
  ProjectCoverageDomain,
} from '@kirin/api-client/contracts/projects';
import { MODE_LABELS } from '@/components/policylist/constants';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { STATUS_LABEL, statusKey, type StatusKey } from './coverageTableView';

/**
 * Shared coverage-display primitives for the Projects surface (KIR-3356) — used
 * by the single-project page, the Policy coverage column, and the inventory row
 * drawer so all three read as one visual system.
 */

/** Number of "gaps": missing + partial categories (the tab's default sort key). */
function coverageGaps(counts: CoverageCounts): number {
  return counts.missing + counts.partial;
}

const MODE_BADGE_CLASS: Record<string, string> = {
  off: 'text-muted-foreground',
  monitor: 'border-blue-500/40 text-blue-600 dark:text-blue-400',
  enforce: 'border-green-500/40 text-green-600 dark:text-green-400',
};

/** Off / Monitor / Enforce pill for a policy domain. */
export function ModeBadge({ mode }: { mode: string }): ReactElement {
  return (
    <Badge variant="outline" className={cn('shrink-0', MODE_BADGE_CLASS[mode])}>
      {MODE_LABELS[mode as keyof typeof MODE_LABELS] ?? mode}
    </Badge>
  );
}

/**
 * Thin covered/partial/missing proportion bar for a single domain card; neutral
 * when nothing has been scanned. Normalizes over the scanned categories only.
 */
export function CoverageBar({
  summary,
}: {
  summary: ProjectCoverageDomain['summary'];
}): ReactElement {
  const scanned = summary.covered + summary.partial + summary.missing;
  if (scanned === 0) {
    return <div className="h-1.5 w-full rounded-full bg-muted" />;
  }
  const pct = (n: number): string => `${(n / scanned) * 100}%`;
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div className="bg-green-500" style={{ width: pct(summary.covered) }} />
      <div className="bg-amber-500" style={{ width: pct(summary.partial) }} />
      <div className="bg-red-500" style={{ width: pct(summary.missing) }} />
    </div>
  );
}

/**
 * Wider stacked bar for the Policy coverage list column: covered / partial /
 * missing segments over the full audited set, with the not-scanned remainder
 * left as the muted track (the grey tail in the mockup). A trailing label reads
 * "N gaps" (missing + partial) or "All covered" when there are none.
 */
export function PolicyCoverageBar({
  coverage,
}: {
  coverage: CoverageCounts;
}): ReactElement {
  const { covered, partial, missing, total } = coverage;
  const gaps = coverageGaps(coverage);

  // No audited categories at all — nothing to render a proportion from.
  if (total === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const pct = (n: number): string => `${(n / total) * 100}%`;
  const scanned = covered + partial + missing;

  return (
    <div className="flex items-center gap-2">
      <div className="flex h-2 w-40 overflow-hidden rounded-full bg-muted">
        <div className="bg-green-500" style={{ width: pct(covered) }} />
        <div className="bg-amber-500" style={{ width: pct(partial) }} />
        <div className="bg-red-500" style={{ width: pct(missing) }} />
      </div>
      {gaps > 0 ? (
        <span className="whitespace-nowrap text-sm font-medium text-red-600 dark:text-red-400">
          {gaps} gap{gaps === 1 ? '' : 's'}
        </span>
      ) : scanned > 0 ? (
        <span className="whitespace-nowrap text-sm font-medium text-green-600 dark:text-green-400">
          All covered
        </span>
      ) : (
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          Not scanned
        </span>
      )}
    </div>
  );
}

/** Icon + tone per coverage status. Keyed so the four cases can't drift apart. */
const STATUS_PRESENTATION: Record<
  StatusKey,
  { Icon: LucideIcon; className: string }
> = {
  covered: {
    Icon: CheckCircle2,
    className: 'font-medium text-green-600 dark:text-green-400',
  },
  partial: {
    Icon: AlertTriangle,
    className: 'font-medium text-amber-600 dark:text-amber-400',
  },
  missing: {
    Icon: XCircle,
    className: 'font-medium text-red-600 dark:text-red-400',
  },
  notScanned: { Icon: Minus, className: 'text-muted-foreground' },
};

/**
 * A category's per-project coverage status as an icon + label. A `null` status
 * means the scanner has produced no row for the category yet ("Not scanned") —
 * the contract has no enum member for it (KIR-3517).
 */
export function StatusCell({
  status,
}: {
  status: CoverageStatus | null;
}): ReactElement {
  const key = statusKey(status);
  const { Icon, className } = STATUS_PRESENTATION[key];
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <Icon className="size-4" />
      {STATUS_LABEL[key]}
    </span>
  );
}
